import React from 'react'
import { Helmet } from 'react-helmet'
import { helmetJsonLdProp } from 'react-schemaorg'
import { Article, BreadcrumbList } from 'schema-dts'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'

import { ModifiedPressArticlePage } from 'types/press'
import { TwitterSEO } from './OpenGraphSEO'
import NoIndex from './NoIndex'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'
import useBrand from '../../hooks/useBrand'

interface Props {
  pressArticle: ModifiedPressArticlePage
  shouldRenderNoIndex: boolean
}

const PressArticleSEO: React.FC<Props> = ({
  pressArticle,
  shouldRenderNoIndex,
}) => {
  const siteBrand = useBrand()
  const { urlPathBuilder, baseURL } = useUrlPathBuilder()

  const {
    seo,
    canonicalUrl,
    body: { raw },
    slug,
    title,
    publishedDate,
    updatedAt,
  } = pressArticle
  const { pageTitle, description, imageURL } = seo
  const brand = siteBrand ?? pressArticle.brand ?? {}
  const plainTextArticle = documentToPlainTextString(JSON.parse(raw))
  const articlePrefix = 'press'
  const pageURL = urlPathBuilder([slug], {
    baseURL: siteBrand.productionUrl,
  })
  return <>
    <Helmet
      script={[
        helmetJsonLdProp<Article>({
          '@context': 'https://schema.org',
          '@type': 'Article',
          name: title,
          headline: title,
          datePublished: publishedDate,
          dateModified: updatedAt,
          articleBody: plainTextArticle,
          wordCount: plainTextArticle.split(/\s+/).length,
          publisher: {
            '@id': urlPathBuilder([], {
              baseURL,
              hash: 'organization',
            }),
          },
          copyrightHolder: {
            '@id': urlPathBuilder([], {
              baseURL,
              hash: 'organization',
            }),
          },
        }),
        helmetJsonLdProp<BreadcrumbList>({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@type': 'WebPage',
                '@id': urlPathBuilder([], { baseURL }),
                url: urlPathBuilder([], { baseURL }),
                name: `${brand.name} Home Page`,
              },
            },
            {
              '@type': 'ListItem',
              position: 2,
              item: {
                '@type': 'WebPage',
                '@id': urlPathBuilder([articlePrefix], { baseURL }),
                url: urlPathBuilder([articlePrefix], { baseURL }),
                name: `${brand.name} Press`,
              },
            },
            {
              '@type': 'ListItem',
              position: 3,
              item: {
                '@type': 'WebPage',
                '@id': urlPathBuilder([slug], {
                  baseURL,
                }),
                url: urlPathBuilder([slug], {
                  baseURL,
                }),
                name: title,
              },
            },
          ],
        }),
      ]}
    >
      <title>{pageTitle ?? title}</title>
      {description && <meta name="description" content={description} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {/* OpenGraph */}
      <meta property="og:site_name" content={brand.name} />
      <meta property="og:type" content="article" />
      <meta name="article:published_time" content={publishedDate} />
      <meta name="article:modified_time" content={updatedAt} />
      <meta property="og:title" content={title} />
      <meta property="og:url" content={pageURL} />
      {imageURL && <meta property="og:image" content={`https:${imageURL}`} />}
    </Helmet>
    {/* Twitter OpenGraph */}
    <TwitterSEO title={title} description={description} />
    {shouldRenderNoIndex && <NoIndex seo={seo} slug={slug} />}
  </>;
}

export default PressArticleSEO
